import React, { JSX } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import "./file-row.css";
import { eyeIcon } from "utils/constant";

interface FileRowProps {
  file?: any;
  pdfTitle?: string;
  documentComponent?: JSX.Element;
  fileName?: string;
  setFileViewer2: (value: boolean) => void;
  fileViewerNumber: number;
  handleRemove?: (fileName: string) => void;
  setSelectedApplicantDocument?: (document: any) => void;
  onViewFile?: (file: any) => void;
  w?:string
}

const FileRow: React.FC<FileRowProps> = ({
  file,
  pdfTitle,
  documentComponent,
  fileName,
  setFileViewer2,
  fileViewerNumber,
  handleRemove,
  setSelectedApplicantDocument,
  onViewFile,
  w
}) => {
  const handleView = () => {
    if (file && setSelectedApplicantDocument) {
      // Prepare the document data for FileViewer
      const documentData = {
        name: file.document_path?.split("/").pop() || file.name || "Unknown",
        size: file.size || 0,
        url: file.document_path || file.url || "",
        isFetched: true,
        type: file.document_type || file.type || "evidence_file",
        uuid: file.document_uuid,
      };

      setSelectedApplicantDocument(documentData);
      setFileViewer2(true);
    }
  };

  // Get file name from different possible properties
  const getFileName = () => {
    if (file) {
      return (
        file.document_path?.split("/").pop() ||
        file.name ||
        pdfTitle ||
        "Unknown File"
      );
    }
    return pdfTitle || "Document";
  };

  // Get file size if available
  const getFileSize = () => {
    if (file?.size) {
      return `${(file.size / 1024).toFixed(2)} KB`;
    }
    return "";
  };

  return (
    <div className={`file-row ${w} w-full`}>
      <div className="file-info">
        <div className="file-details flex">
          <svg
            width="29"
            height="30"
            viewBox="0 0 29 30"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <g clip-path="url(#clip0_13_4883)">
              <path
                d="M6.76672 15.9667H5.80005V14.0333H6.76672C7.02309 14.0333 7.26897 14.1352 7.45025 14.3165C7.63154 14.4977 7.73338 14.7436 7.73338 15C7.73338 15.2564 7.63154 15.5022 7.45025 15.6835C7.26897 15.8648 7.02309 15.9667 6.76672 15.9667ZM13.5334 19.8333V14.0333H14.5C14.7564 14.0333 15.0023 14.1352 15.1836 14.3165C15.3649 14.4977 15.4667 14.7436 15.4667 15V18.8667C15.4667 19.123 15.3649 19.3689 15.1836 19.5502C15.0023 19.7315 14.7564 19.8333 14.5 19.8333H13.5334Z"
                fill="#432101"
              />
              <path
                fill-rule="evenodd"
                clip-rule="evenodd"
                d="M1.93359 3.4C1.93359 2.63087 2.23913 1.89325 2.78298 1.34939C3.32684 0.805535 4.06447 0.5 4.83359 0.5L20.7005 0.5L27.0669 6.86647V26.6C27.0669 27.3691 26.7614 28.1068 26.2175 28.6506C25.6737 29.1945 24.9361 29.5 24.1669 29.5H4.83359C4.06447 29.5 3.32684 29.1945 2.78298 28.6506C2.23913 28.1068 1.93359 27.3691 1.93359 26.6V3.4ZM6.76693 12.1H3.86693V21.7667H5.80026V17.9H6.76693C7.53605 17.9 8.27368 17.5945 8.81754 17.0506C9.36139 16.5068 9.66693 15.7691 9.66693 15C9.66693 14.2309 9.36139 13.4932 8.81754 12.9494C8.27368 12.4055 7.53605 12.1 6.76693 12.1ZM14.5003 12.1H11.6003V21.7667H14.5003C15.2694 21.7667 16.007 21.4611 16.5509 20.9173C17.0947 20.3734 17.4003 19.6358 17.4003 18.8667V15C17.4003 14.2309 17.0947 13.4932 16.5509 12.9494C16.007 12.4055 15.2694 12.1 14.5003 12.1ZM19.3336 21.7667V12.1H25.1336V14.0333H21.2669V15.9667H23.2003V17.9H21.2669V21.7667H19.3336Z"
                fill="#432101"
              />
            </g>
            <defs>
              <clipPath id="clip0_13_4883">
                <rect
                  width="29"
                  height="29"
                  fill="white"
                  transform="translate(0 0.5)"
                />
              </clipPath>
            </defs>
          </svg>

          <p className="file-name break-words w-52">{getFileName()}</p>
          {getFileSize() && <span className="file-size">{getFileSize()}</span>}
        </div>
      </div>

      <div className="file-actions">
        {fileViewerNumber === 2 && (
          <button
            onClick={handleView}
            className="view-button"
            style={{ background: "none",width:"fit-content" }}
            type="button"
          >
            {eyeIcon}{" "}
          </button>
        )}

        {fileViewerNumber === 1 && file && (
          <button
            onClick={() => handleRemove?.(file.name)}
            className="remove-button"
            type="button"
          >
            <svg
              className="w-5 h-5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
              />
            </svg>
          </button>
        )}

        {fileViewerNumber === 3 && (
          <PDFDownloadLink document={documentComponent!} fileName={fileName!}>
            {({ loading }) =>
              loading ? (
                <span>Generating...</span>
              ) : (
                <button className="button-download" type="button">
                  Download
                </button>
              )
            }
          </PDFDownloadLink>
        )}
      </div>
    </div>
  );
};

export default FileRow;
